import { v4 as uuidv4 } from 'uuid';
import fetchPageInfo from './pageRouter';
import { getCookie, setCookie } from './cookie';
import { TRACKING_SESSION_KEY, USER_COUNTRY_CODE, ACTIVE_PROFILE_ID } from '../constants/cookieConstants';

const website_version = process.env.WEBSITE_VERSION || 'local';
export const trackSearch = (item, value) => ({
  name: TRACK_EVENTS.SEARCH_TRACK,
  item: SEARCH_LABELS.DYNAMIC_FIELDS[item] || item,
  value,
});

export const trackSort = value => ({
  name: TRACK_EVENTS.SEARCH_SORT,
  sort_by: SORT_LABELS[value] || value,
});

export const trackClearForm = () => ({
  name: TRACK_EVENTS.SEARCH_CLEAR,
});

export const trackSearchFilter = (filter_on, filter_value) => ({
  name: TRACK_EVENTS.SEARCH_LIST_FILTER,
  filter_on: SEARCH_FILTER_LABELS.DYNAMIC_FIELDS[filter_on] || filter_on,
  filter_value,
});

export const trackSearchGo = query => ({
  name: TRACK_EVENTS.SEARCH_GO,
  ...formatQuery(query),
});

export const trackUserAction = (payload, router) => {
  const url = router.asPath;
  const path = router.pathname === '/' ? '/' : url.slice(0, url.lastIndexOf('/'));
  const pageInfo = fetchPageInfo(path, router.query);
  const data = {};
  data.page_type = pageInfo && pageInfo.page_type ? pageInfo.page_type : url;
  data.session_id = generateSessionId();
  data.website_version = website_version;
  data.req_country_code = getCountryCode();
  data.req_profile_id = getProfileId();
  return { ...payload, ...data };
};

const inThirtyMinutes = () => new Date(new Date().getTime() + 30 * 60 * 1000);

export const generateSessionId = () => {
  let sessionId = getCookie(TRACKING_SESSION_KEY);
  if (!sessionId) {
    sessionId = uuidv4();
    setCookie(TRACKING_SESSION_KEY, sessionId, inThirtyMinutes());
  }
  return sessionId;
};

export const updateSessionIdInCookie = () => {
  const sessionId = getCookie(TRACKING_SESSION_KEY) || uuidv4();
  setCookie(TRACKING_SESSION_KEY, sessionId, inThirtyMinutes());
};

export const getCountryCode = () => getCookie(USER_COUNTRY_CODE);

export const userIdentifyEvent = (userData, profileId) => {
  const { name, email, id } = userData;
  const traits = {
    name,
    email,
    req_profile_id: profileId,
    req_country_code: getCountryCode(),
    website_version,
    session_id: generateSessionId(),
  };
  if (typeof window !== 'undefined' && window.analytics) {
    window.analytics.identify(id, traits);
  }
};

export const resetUserOnLogout = () => {
  if (typeof window !== 'undefined' && window.analytics) {
    window.analytics.reset();
  }
};

export const pageTracking = (router, url) => {
  if (typeof window !== 'undefined' && window.analytics) {
    const path = router.pathname === '/' ? '/' : url.slice(0, url.lastIndexOf('/'));
    const pageInfo = fetchPageInfo(path, router.query);
    pageInfo.session_id = generateSessionId();
    pageInfo.req_country_code = getCountryCode();
    pageInfo.req_profile_id = getProfileId();
    window.analytics.page({ properties: pageInfo });
  }
};

const getProfileId = () => {
  return getCookie(ACTIVE_PROFILE_ID) || null;
};

export const TRACK_EVENTS = {
  SEARCH_TRACK: 'search_track',
  SEARCH_GO: 'search_go',
  SEARCH_CLEAR: 'search_clear',
  SEARCH_SORT: 'search_sort',
  SEARCH_LIST_FILTER: 'search_list_filter',
};

export const formatQuery = query => {
  const obj = {};
  for (const [key, value] of Object.entries(query)) {
    const newKey = SEARCH_LABELS.DYNAMIC_FIELDS[key] || key;
    const isKeyExist = searchExhaustiveList.find(value => value === newKey);
    if (isKeyExist) {
      obj[newKey] = value;
    } else {
      obj.other = {
        key: newKey,
        value,
      };
    }
  }
  return obj;
};

export const searchExhaustiveList = [
  'country',
  'city',
  'company',
  'composer',
  'work',
  'from',
  'to',
  'voice',
  'profession',
  'date',
  'status',
  'age_limit_men',
  'age_limit_women',
  'year',
  'seasons',
  'production',
];

export const SEARCH_LABELS = {
  DYNAMIC_FIELDS: {
    country_id: 'country',
    date_from: 'from',
    date_to: 'to',
    city_id: 'city',
    composer_id: 'composer',
    company_id: 'company',
    work_id: 'work',
    managers_id: 'manager',
    competition_id: 'competition',
    city: 'city',
    status: 'status',
    age_limit_men: 'age_limit_men',
    age_limit_women: 'age_limit_women',
    year: 'year',
  },
  COMPANY: 'company',
  MANAGER: 'manager',
  COMPETITIONS: 'competitions',
  DATE: 'date',
  ARTIST: 'artist',
  FESTIVALS: 'festivals',
  VENUES: 'venues',
};

export const SORT_LABELS = {
  year: 'performance_date',
  'productions.name': 'musical_work_name',
  'workComposer.name': 'composer_name',
  'country.name': 'country_name',
  'city.name': 'city_name',
  'conductor.name': 'conductor_name',
  'director.name': 'director_name',
  'company.name': 'company_name',
};

export const SEARCH_FILTER_LABELS = {
  COUNTRY: 'country',
  SEASONS: 'seasons',
  DYNAMIC_FIELDS: {
    profession_id: 'profession',
    profession_qualification_id: 'voice',
    country_id: 'country',
    years: 'years',
    work_id: 'work',
    year: 'year',
    company_id: 'company',
  },
};
